import React, { createContext, useContext, useState, useCallback } from 'react';

// Create the context
const AppContext = createContext();

// Custom hook to use the context
export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};

// Context provider component
export const AppProvider = ({ children }) => {
  // Global state for customer, fleet, and canopy contexts
  const [customerContext, setCustomerContext] = useState(null);
  const [fleetContext, setFleetContext] = useState(null);
  const [canopyContext, setCanopyContext] = useState(null);
  const [contextError, setContextError] = useState(null);

  // Set customer context (clears fleet and canopy contexts when customer changes)
  const setCustomer = useCallback((customer) => {
    setCustomerContext(customer);
    setFleetContext(null); // Clear fleet context when customer changes
    setCanopyContext(null); // Clear canopy context when customer changes
    setContextError(null);
  }, []);

  // Set fleet context (validates customer context exists, clears canopy context)
  const setFleet = useCallback((fleet) => {
    if (!customerContext) {
      setContextError('Please select a customer before selecting a fleet');
      return false;
    }
    if (!fleet || (fleet.customerId && fleet.customerId !== customerContext.customerId)) {
      setContextError('This fleet is not available in the selected account');
      return false;
    }
    setFleetContext(fleet);
    if (fleetContext?.fleetId !== fleet.fleetId) setCanopyContext(null);
    setContextError(null);
    return true;
  }, [customerContext, fleetContext?.fleetId]);

  // Set canopy context (validates customer and fleet contexts exist)
  const setCanopy = useCallback((canopy) => {
    if (!customerContext) {
      setContextError('Please select a customer before selecting a canopy');
      return false;
    }
    if (!fleetContext) {
      setContextError('Please select a fleet before selecting a canopy');
      return false;
    }
    setCanopyContext(canopy);
    setContextError(null);
    return true;
  }, [customerContext, fleetContext]);

  // Clear customer context (also clears fleet and canopy contexts)
  const clearCustomer = useCallback(() => {
    setCustomerContext(null);
    setFleetContext(null);
    setCanopyContext(null);
    setContextError(null);
  }, []);

  // Clear fleet context (also clears canopy context)
  const clearFleet = useCallback(() => {
    setFleetContext(null);
    setCanopyContext(null);
    setContextError(null);
  }, []);

  // Clear canopy context only
  const clearCanopy = useCallback(() => {
    setCanopyContext(null);
    setContextError(null);
  }, []);

  // Validate context for operations
  const validateContext = useCallback((requiredContext) => {
    switch (requiredContext) {
      case 'customer':
        if (!customerContext) {
          setContextError('Please select a customer before performing this operation');
          return false;
        }
        break;
      case 'fleet':
        if (!customerContext) {
          setContextError('Please select a customer before performing this operation');
          return false;
        }
        if (!fleetContext) {
          setContextError('Please select a fleet before performing this operation');
          return false;
        }
        break;
      case 'canopy':
        if (!customerContext) {
          setContextError('Please select a customer before performing this operation');
          return false;
        }
        if (!fleetContext) {
          setContextError('Please select a fleet before performing this operation');
          return false;
        }
        if (!canopyContext) {
          setContextError('Please select a canopy before performing this operation');
          return false;
        }
        break;
      default:
        return true;
    }
    setContextError(null);
    return true;
  }, [customerContext, fleetContext, canopyContext]);

  // Get current context data for API calls
  const getContextData = useCallback(() => {
    return {
      customerId: customerContext?.customerId,
      customerName: customerContext?.customerName,
      fleetId: fleetContext?.fleetId,
      fleetName: fleetContext?.fleetName,
      canopyId: canopyContext?.canopyId,
      canopyName: canopyContext?.canopyName,
      hasCustomer: !!customerContext,
      hasFleet: !!fleetContext,
      hasCanopy: !!canopyContext
    };
  }, [customerContext, fleetContext, canopyContext]);

  // Clear any context errors
  const clearError = useCallback(() => {
    setContextError(null);
  }, []);

  const value = {
    // State
    customerContext,
    fleetContext,
    canopyContext,
    contextError,
    
    // Actions
    setCustomer,
    setFleet,
    setCanopy,
    clearCustomer,
    clearFleet,
    clearCanopy,
    validateContext,
    getContextData,
    clearError
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

export default AppContext;
